import React, { useState } from 'react';
import { Card, Button, Form, Alert, Spinner, Row, Col, Badge } from 'react-bootstrap';

const ABNValidation = () => {
  const [abn, setAbn] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const handleValidation = async () => {
    if (!abn.trim()) {
      alert('Please enter an ABN to validate');
      return;
    }

    setIsValidating(true);
    setValidationResult(null);

    try {
      const response = await fetch('/api/validate-abn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ abn: abn.trim() }),
      });

      const result = await response.json();
      setValidationResult(result);
    } catch (error) {
      setValidationResult({
        valid: false,
        error: 'Validation failed: ' + error.message
      });
    } finally {
      setIsValidating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU');
  };

  return (
    <div className="form-section">
      <h3>
        <i className="fas fa-search me-2"></i>
        ABN Validation with ABR Lookup
      </h3>
      
      <Card className="mb-4">
        <Card.Header>Validate ABN</Card.Header>
        <Card.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Australian Business Number (ABN)</Form.Label>
                <Form.Control
                  type="text"
                  value={abn}
                  onChange={(e) => setAbn(e.target.value)}
                  placeholder="Enter 11-digit ABN (e.g., 51 824 753 556)"
                  maxLength="14"
                />
                <Form.Text className="text-muted">
                  Enter ABN with or without spaces
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button
                variant="primary"
                onClick={handleValidation}
                disabled={isValidating || !abn.trim()}
                className="w-100"
              >
                {isValidating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Validating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-1"></i>
                    Validate ABN
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {validationResult && (
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>Validation Results</span>
            <Badge bg={validationResult.valid ? 'success' : 'danger'}>
              {validationResult.valid ? 'Valid' : 'Invalid'}
            </Badge>
          </Card.Header>
          <Card.Body>
            {validationResult.error ? (
              <Alert variant="danger">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Validation Error:</strong> {validationResult.error}
              </Alert>
            ) : (
              <div>
                {/* Basic Validation Info */}
                <Row className="mb-3">
                  <Col md={6}>
                    <strong>ABN:</strong> {validationResult.abn}
                  </Col>
                  <Col md={6}>
                    <strong>Checksum Valid:</strong> 
                    <Badge bg={validationResult.checksumValid ? 'success' : 'danger'} className="ms-2">
                      {validationResult.checksumValid ? 'Yes' : 'No'}
                    </Badge>
                  </Col>
                </Row>

                {/* ABR Data */}
                {validationResult.abrLookup && validationResult.abrLookup.success ? (
                  <div>
                    <h5 className="mb-3">
                      <i className="fas fa-building me-2"></i>
                      Australian Business Register Information
                    </h5>
                    
                    <Row className="mb-3">
                      <Col md={6}>
                        <strong>Entity Name:</strong>
                        <div className="text-muted">{validationResult.abrLookup.entityName || 'N/A'}</div>
                      </Col>
                      <Col md={6}>
                        <strong>Entity Type:</strong>
                        <div className="text-muted">{validationResult.abrLookup.entityType || 'N/A'}</div>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <strong>Status:</strong>
                        <Badge bg={validationResult.abrLookup.isActive ? 'success' : 'danger'} className="ms-2">
                          {validationResult.abrLookup.status || 'N/A'}
                        </Badge>
                      </Col>
                      <Col md={6}>
                        <strong>GST Status:</strong>
                        <Badge bg={validationResult.abrLookup.isGSTRegistered ? 'success' : 'warning'} className="ms-2">
                          {validationResult.abrLookup.gstStatus || 'N/A'}
                        </Badge>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <strong>Effective From:</strong>
                        <div className="text-muted">{formatDate(validationResult.abrLookup.effectiveFrom)}</div>
                      </Col>
                      <Col md={6}>
                        <strong>Effective To:</strong>
                        <div className="text-muted">{formatDate(validationResult.abrLookup.effectiveTo) || 'Current'}</div>
                      </Col>
                    </Row>
                  </div>
                ) : (
                  <Alert variant="warning">
                    <i className="fas fa-info-circle me-2"></i>
                    ABR lookup not available or failed. Using basic validation only.
                  </Alert>
                )}

                {/* Summary */}
                <div className="mt-4 p-3 bg-light rounded">
                  <h6 className="mb-2">Summary:</h6>
                  <ul className="mb-0">
                    <li>ABN format and checksum: {validationResult.checksumValid ? 'Valid' : 'Invalid'}</li>
                    <li>ABR registration: {validationResult.abrLookup?.success ? 'Found' : 'Not found/Failed'}</li>
                    <li>Entity active: {validationResult.isActive ? 'Yes' : 'No'}</li>
                    <li>GST registered: {validationResult.isGSTRegistered ? 'Yes' : 'No'}</li>
                  </ul>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default ABNValidation;
